import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import {
    BarChart4,
    Book,
    Calendar1,
    ChevronRight,
    LayoutDashboard,
    LineChart,
    Link2,
    Map,
    MapPin,
    Mic2,
    Music,
    Music4,
    Radio,
    Search,
    UserCircle2,
    Users2,
    X,
    Zap,
} from "lucide-react"
import { generateMetadata } from "../seo/metadataGenerator"
import { generateBreadcrumbSchema, generateFAQPageSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator"
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants"
import { ReactComponent as MarketingManagerIcon } from '../assets/images/icons/growth-engine.svg'
import { ReactComponent as ArtistIcon } from '../assets/images/icons/artist-manager.svg'
import { ReactComponent as AudioIcon } from '../assets/images/icons/urstudio.svg'
import { ReactComponent as AnalystIcon } from '../assets/images/icons/data-analyst.svg'
import { ReactComponent as ProfessorUIcon } from '../assets/images/icons/professoru.svg'
import { ReactComponent as AmplitudeIcon } from '../assets/images/icons/amplitude.svg'
import { ReactComponent as DistributionIcon } from '../assets/images/icons/distribution-new.svg'
import { ReactComponent as MixEngineerIcon } from '../assets/images/icons/mixing-icon.svg';
import { ReactComponent as MasteringEngineerIcon } from '../assets/images/icons/mastering-icon.svg';
import { ReactComponent as MixEnhancerIcon } from '../assets/images/icons/mix-enhancer-icon.svg';
import { ReactComponent as MixAnalystIcon } from '../assets/images/icons/mix-analyst-icon.svg';
import { ReactComponent as ResourceIcon } from '../assets/images/icons/resource-library.svg';
import SeoHead from "../seo/SeoHead"
import { usePrerenderReady } from "../hooks/usePrerenderReady"
import {
    GETTING_STARTED_TOUR_PATH,
    getProfessorUModules,
    professorULessons,
} from "./admin/ProfessorU/lessonsData"

const THEME_ICON_MAP = {
    "Getting Started": Zap,
    "Dashboard": LayoutDashboard,
    "Amplitude AI": AmplitudeIcon,
    "UrStudio": AudioIcon,
    "Mastering Engineer": MasteringEngineerIcon,
    "Mixing Engineer": MixEngineerIcon,
    "Mix Enhancer": MixEnhancerIcon,
    "Mix Analyst": MixAnalystIcon,
    "Distribution Account Setup": DistributionIcon,
    "Creating & Publishing A Release": DistributionIcon,
    "Growth Engine": MarketingManagerIcon,
    "Create Campaign": MarketingManagerIcon,
    "Campaign History": MarketingManagerIcon,
    "Campaign Editor": BarChart4,
    "Artist Manager": ArtistIcon,
    "Release and Financial Planner": Calendar1,
    "Link And Fan Hub": Link2,
    "Build Bio Link": UserCircle2,
    "Build Music Link": Music4,
    "Build Tour Link": Mic2,
    "Build Pre-Save Page": Users2,
    "Link Analytics": LineChart,
    "Data Analyst": AnalystIcon,
    "Data Analyst Catalog": Map,
    "Milestones Activities": MapPin,
    "Radio Play Activities": Radio,
    "ProfessorU": ProfessorUIcon,
    "Guide by Module": ProfessorUIcon,
    "Artist Interviews and Talks with Industry Experts": ProfessorUIcon,
    "Module Walkthroughs": ProfessorUIcon,
    "ProfessorU Rewards": Music,
    "ProfessorU Resource Library": ResourceIcon,
    "Distribution": DistributionIcon,
}

const getThemeIcon = (name, fallback) => THEME_ICON_MAP[name] || fallback || Book

const normalizeSearch = (value = "") => String(value || "").toLowerCase().trim()

const matchesQuery = (query, ...values) => {
    const needle = normalizeSearch(query)
    if (!needle) return true
    return values.some((value) => normalizeSearch(value).includes(needle))
}

function LessonIcon({ Icon, className }) {
    if (!Icon) return null
    return <Icon className={className} />
}

export default function TutorialsPage() {
    usePrerenderReady()
    const navigate = useNavigate()
    const [selectedCategory, setSelectedCategory] = useState("all")
    const [searchQuery, setSearchQuery] = useState("")
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 })
    const [isHovering, setIsHovering] = useState(false)

    const modules = useMemo(() => getProfessorUModules(professorULessons), [])
    const totalLessons = professorULessons.length

    const themedModules = useMemo(() =>
        modules.map((module) => ({
            ...module,
            icon: getThemeIcon(module.label, module.icon),
            tours: module.tours.map((lesson) => ({
                ...lesson,
                icon: getThemeIcon(lesson.name, getThemeIcon(module.label, lesson.thumbnail)),
            })),
        })),
    [modules])

    const categories = useMemo(() => [
        { id: "all", label: "All Tutorials", count: totalLessons, icon: Book },
        ...themedModules.map((module) => ({
            id: module.id,
            label: module.label,
            count: module.count,
            icon: module.icon,
        })),
    ], [themedModules, totalLessons])

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY })
        })
    }, [])

    useEffect(() => {
        document.body.style.cursor = "none"
        const handleMouseEnter = () => setIsHovering(true)
        const handleMouseLeave = () => setIsHovering(false)

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        )
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter)
            element.addEventListener("mouseleave", handleMouseLeave)
        })

        window.addEventListener("mousemove", updateCursorPosition)

        return () => {
            document.body.style.cursor = "auto"
            window.removeEventListener("mousemove", updateCursorPosition)
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter)
                element.removeEventListener("mouseleave", handleMouseLeave)
            })
        }
    }, [updateCursorPosition])

    const isSearching = normalizeSearch(searchQuery).length > 0

    const visibleModules = useMemo(() => {
        const query = searchQuery.trim()

        if (isSearching) {
            return themedModules
                .map((module) => {
                    const moduleMatched = matchesQuery(query, module.label, module.id, module.moduleNumber)
                    return {
                        ...module,
                        tours: moduleMatched
                            ? module.tours
                            : module.tours.filter((lesson) =>
                                matchesQuery(
                                    query,
                                    lesson.title,
                                    lesson.name,
                                    lesson.moduleNumber,
                                    lesson.subNumber != null ? `${lesson.moduleNumber}.${lesson.subNumber}` : "",
                                )
                            ),
                    }
                })
                .filter((module) => module.tours.length > 0)
        }

        if (selectedCategory === "all") return themedModules
        return themedModules.filter((module) => module.id === selectedCategory)
    }, [themedModules, selectedCategory, isSearching, searchQuery])

    const visibleLessonCount = useMemo(
        () => visibleModules.reduce((count, module) => count + module.tours.length, 0),
        [visibleModules]
    )

    const startLesson = (lesson) => {
        if (!lesson.url) {
            navigate(`${GETTING_STARTED_TOUR_PATH}#getting-started`)
            return
        }
        navigate(`${lesson.url}#start-tour`)
    }

    const selectedLabel = selectedCategory === "all"
        ? "All Tutorials"
        : categories.find((category) => category.id === selectedCategory)?.label

    const metadata = generateMetadata({
        title:
            "Tutorials & Artist Growth Guides | UpNComer",

        description:
            "Access step-by-step tutorials on music marketing, distribution, playlist promotion, artist branding, analytics, fan engagement and AI-powered growth strategies. UpNComer helps independent artists learn proven workflows, release strategies and career-building techniques to grow audiences and succeed in the music industry.",

        image: PAGE_OG_IMAGES.tutorials,

        path: "/tutorials",
    })

    const webPageSchema = generateWebPageSchema({
        title:
            "Tutorials & Artist Growth Guides | UpNComer",

        description:
            "Access step-by-step tutorials on music marketing, distribution, playlist promotion, artist branding, analytics, fan engagement and AI-powered growth strategies. UpNComer helps independent artists learn proven workflows, release strategies and career-building techniques to grow audiences and succeed in the music industry.",

        url: `${SITE_URL}/tutorials`,
    })

    const breadcrumbItems = [
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Tutorials",
            "path": "/tutorials"
        }
    ]

    const schemas = generateGraphSchema([
        webPageSchema,
        generateBreadcrumbSchema(breadcrumbItems),
        generateFAQPageSchema()
    ])

    return (
        <>
            <SeoHead
                metadata={metadata}
                schemas={schemas}
            />
            <div
                className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                style={{
                    left: `${cursorPosition.x}px`,
                    top: `${cursorPosition.y}px`,
                    transform: `translate(-50%, -50%)`,
                }}
            >
                <div className="inner-circle"></div>
            </div>
            <div
                className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden"
            >
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.3, 0.6, 0.3],
                        }}
                        transition={{
                            duration: 10,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                        }}
                        className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                    />

                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.2, 0.5, 0.2],
                        }}
                        transition={{
                            duration: 12,
                            repeat: Number.POSITIVE_INFINITY,
                            ease: "easeInOut",
                            delay: 3,
                        }}
                        className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                    />
                </div>

                <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
                    <div className="container max-w-7xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center mb-16"
                        >
                            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                Tutorials & Guides
                            </h1>
                            <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed mb-8">
                                Master UpNComer's tools and grow your music career with our comprehensive interactive tutorials and guides
                            </p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="relative max-w-2xl mx-auto"
                            >
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#3b82f6]" />
                                    <input
                                        type="text"
                                        placeholder="Search modules or lessons..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        autoComplete="off"
                                        className="w-full pl-12 pr-12 py-4 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] placeholder-[#818cf8]/60 focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 focus:border-[#FA6138]/50 backdrop-blur-xl"
                                    />
                                    {isSearching && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#93c5fd] hover:text-[#FA6138] transition-colors"
                                            aria-label="Clear search"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>

                        {!isSearching && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mb-12"
                        >
                            <div>
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                    Browse by Category
                                </h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                    {categories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`w-full h-full min-h-[88px] p-4 rounded-xl text-left transition-all duration-300 ${selectedCategory === category.id
                                                ? "bg-gradient-to-br from-[#FA6138]/20 to-[#e55532]/10 border border-[#FA6138]/30 text-[#FA6138]"
                                                : "bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40"
                                                } backdrop-blur-xl`}
                                        >
                                            <div className="flex items-center mb-2 min-w-0">
                                                <span className="shrink-0">
                                                    <LessonIcon Icon={category.icon} className="w-5 h-5" />
                                                </span>
                                                <span
                                                    title={category.label}
                                                    className="ml-2 font-medium text-xs whitespace-nowrap truncate flex-1 min-w-0"
                                                >
                                                    {category.label}
                                                </span>
                                            </div>
                                            <span className="text-xs opacity-70 whitespace-nowrap">
                                                {category.count} {category.count === 1 ? "lesson" : "lessons"}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                        )}

                        <div className="space-y-12">
                            {isSearching && visibleModules.length > 0 && (
                                <p className="text-[#818cf8]/80 text-sm">
                                    {visibleLessonCount} {visibleLessonCount === 1 ? "lesson" : "lessons"} across {visibleModules.length} {visibleModules.length === 1 ? "module" : "modules"} for “{searchQuery.trim()}”
                                </p>
                            )}
                            {visibleModules.length === 0 ? (
                                <div className="bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-8 text-center backdrop-blur-xl">
                                    <Search className="w-10 h-10 text-[#818cf8]/70 mx-auto mb-3" />
                                    <p className="text-[#93c5fd]">
                                        {isSearching
                                            ? `No modules or lessons match “${searchQuery.trim()}”.`
                                            : "No lessons found."}
                                    </p>
                                </div>
                            ) : (
                                visibleModules.map((module) => (
                                    <section key={module.id}>
                                        {(isSearching || selectedCategory === "all") && (
                                            <div className="flex items-center gap-3 mb-6">
                                                <span className="text-[#93c5fd]">
                                                    <LessonIcon Icon={module.icon} className="w-6 h-6" />
                                                </span>
                                                <div>
                                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent">
                                                        {module.label}
                                                    </h2>
                                                    <p className="text-[#818cf8]/70 text-sm">
                                                        {module.tours.length} {module.tours.length === 1 ? "lesson" : "lessons"}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {!isSearching && selectedCategory !== "all" && (
                                            <h2 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                                {selectedLabel} Lessons
                                            </h2>
                                        )}
                                        <div className="grid grid-cols-[repeat(auto-fill,minmax(242px,1fr))] gap-4">
                                            {module.tours.map((lesson, index) => (
                                                <motion.button
                                                    key={`${module.id}-${lesson.title}`}
                                                    type="button"
                                                    onClick={() => startLesson(lesson)}
                                                    initial={{ opacity: 0, y: 24 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.4) }}
                                                    className="w-full max-w-[242px] h-[278px] mx-auto rounded-xl flex flex-col items-center p-3 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#c7d2fe] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-lg backdrop-blur-xl overflow-visible group"
                                                >
                                                    <div className="flex-1 flex items-center justify-center overflow-hidden w-full rounded-lg">
                                                        <LessonIcon Icon={lesson.icon} className="w-[160px] h-[160px] text-[#93c5fd]" />
                                                    </div>
                                                    <p
                                                        title={lesson.title}
                                                        className="w-full min-w-0 text-center text-sm font-normal mt-2 leading-tight px-1 break-words whitespace-normal"
                                                    >
                                                        {lesson.title}
                                                    </p>
                                                    <span className="inline-flex items-center justify-center mt-2 text-xs font-medium text-[#93c5fd] group-hover:text-[#FA6138] transition-colors">
                                                        Start Tutorial
                                                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                                    </span>
                                                </motion.button>
                                            ))}
                                        </div>
                                    </section>
                                ))
                            )}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.2 }}
                            className="text-center mt-20"
                        >
                            <div
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-12"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                    Ready to Master UpNComer?
                                </h2>
                                <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto text-lg">
                                    Start your learning journey today and unlock the full potential of AI-powered music marketing.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a
                                        href="/signup"
                                        className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                           px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                           transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                                    >
                                        START FREE ACCOUNT
                                    </a>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedCategory("all")
                                            setSearchQuery("")
                                            window.scrollTo({ top: 0, behavior: "smooth" })
                                        }}
                                        className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                           px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                           transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                                    >
                                        <Book className="w-4 h-4 mr-2" />
                                        BROWSE ALL TUTORIALS
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </main>
            </div>
        </>
    )
}
